import {BasketPurchaseJournalDetail} from './BasketPurchaseJournalDetail';
export type {BasketPurchaseJournalDetail} from './BasketPurchaseJournalDetail';
import {BasketPurchaseJournalDetailDto} from './BasketPurchaseJournalDetailDto';
export type {BasketPurchaseJournalDetailDto} from './BasketPurchaseJournalDetailDto';
import {BasketPurchaseJournalPage} from './BasketPurchaseJournalPage';
export type {BasketPurchaseJournalPage} from './BasketPurchaseJournalPage';
import {BasketPurchaseJournalPageDto} from './BasketPurchaseJournalPageDto';
export type {BasketPurchaseJournalPageDto} from './BasketPurchaseJournalPageDto';
import {BasketDto} from './BasketDto';
export type {BasketDto} from './BasketDto';
import {matchesTaggedText} from '../tags/matches-tagged-text';
import {BasketPurchaseDto} from './BasketPurchaseDto';
import {BasketPurchase} from './BasketPurchase';
export type {BasketPurchase} from './BasketPurchase';
import {BasketNeed} from './BasketNeed';
export type {BasketNeed} from './BasketNeed';
import {BasketNeedDto} from './BasketNeedDto';
export type {BasketNeedDto} from './BasketNeedDto';
import {mapTag} from '../tags/map-tag';
import {TagSelection} from '../tags/tag-selection';
import {BasketProductTemplateDto} from './BasketProductTemplateDto';
import {BasketProductTemplateWrite} from './BasketProductTemplateWrite';
import {BasketUnit} from './BasketUnit';
export type {BasketUnit} from './BasketUnit';
import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpContext} from '@angular/common/http';
import {BehaviorSubject, finalize, map, Observable, Subject, tap} from 'rxjs';
import {ApiResponse} from '../auth/auth.models';
import {buildApiUrl} from '../shared/api/api-url';
import {LocalApiErrorCode, LOCALLY_HANDLED_API_ERROR_CODES} from '../shared/errors/local-api-error';
import {PACKAGING_ERROR_CODES} from '../shared/packaging/packaging-correction.service';
import {calculatePriceTotal, ItemPrice, ItemPriceDto, mapItemPrice} from '../shared/prices/currency.service';

export interface BasketSourceLink {sourceItemId?: string | null; sourceContainerId?: string | null}
export interface BasketNeedMeasurement {unitId: string; packagingQuantity: number | null; packagingUnitId: string | null}
export interface BasketProductDetails {barcode: string | null; description: string | null; images: {id: string; originalUrl: string; thumbnailUrl: string | null; isMain: boolean; useInAi: boolean}[]}
export interface BasketPresentationMeasurement {unitId: string; packagingQuantity: number | null; packagingUnitId: string | null; isPackagingCorrection?: boolean}
export interface BasketMeasurementState {product?: BasketProductDetails | null; packagingQuantity?: number | null; packagingUnitId?: string | null; canEditUnit?: boolean; presentationMeasurement?: BasketPresentationMeasurement | null}
export type BasketPriority = 'high' | 'medium' | 'low';
export function isBasketPriority(value: unknown): value is BasketPriority {
  return value === 'high' || value === 'medium' || value === 'low';
}
export type BasketPriorityChange = {mode: 'automatic'; value: null} | {mode: 'manual'; value: BasketPriority};
export interface BasketNeedHistoryEvent {id: string; createdAt: string; actorId: string | null; actorName: string | null; reason: string; version: number; changes: {field: string; before: string | null; after: string | null}[]; unitId?: string | null; presentationMeasurement?: BasketPresentationMeasurement | null}
export interface BasketNeedHistoryPage {needId: string; name: string; contextOwnerId: string; items: BasketNeedHistoryEvent[]; nextCursor: string | null}
interface BasketNeedHistoryEventDto {id: string; createdAt: string; actorId: string | null; actorName: string | null; reason: string; version: number; changes: {field: string; before: string | null; after: string | null}[]; unitId?: string | null; presentationMeasurement?: BasketPresentationMeasurement | null}
interface BasketNeedHistoryPageDto {needId: string; name: string; contextOwnerId: string; items: BasketNeedHistoryEventDto[]; nextCursor: string | null}

export interface BasketNeedRecipient {userId: string; displayName: string}
export type BasketTab = 'all' | 'manual' | 'automatic' | 'outgoing' | 'incoming';
export type BasketPriorityFilter = 'all' | BasketPriority;

export interface BasketPurchaseLimitDto {currentCount: number; limit: number; isUnlimited: boolean; isLimitReached: boolean; resetsAt: string}
export interface BasketPurchaseLimit {currentCount: number; limit: number; unlimited: boolean; reached: boolean; resetsAt: string}



export interface Basket {containerId: string; canCreate: boolean; items: BasketNeed[]; units: BasketUnit[]; purchases?: BasketPurchase[]; purchaseLimit?: BasketPurchaseLimit | null}
export {BasketProductTemplateWrite} from './BasketProductTemplateWrite';
export {BasketProductTemplateDto} from './BasketProductTemplateDto';
export function mapBasketProduct(product: BasketProductTemplateWrite): BasketProductTemplateDto {
  return {barcode: product.barcode ?? null, description: product.description ?? null,
    packagingQuantity: product.packaging ? Number(product.packaging.quantity.replace(',', '.')) : null,
    packagingUnitId: product.packaging?.unitId ?? null,
    ...(product.images ? {images: product.images.map(image => ({isMain: image.isMain, useInAi: image.useInAi, ...(image.existingImageId ? {existingImageId: image.existingImageId} : {}), ...(image.sourceImageId ? {sourceImageId: image.sourceImageId} : {})}))} : {})};
}
export interface BasketWrite {plannedPrice?: ItemPrice | null; clearPlannedPrice?: boolean; measurement?: BasketNeedMeasurement; priorityChange?: BasketPriorityChange; id: string; name: string; unitId: string; quantity: number; sourceItemId: string | null; version: number | null; plannedShopId?: string | null; comment?: string | null; product?: BasketProductTemplateWrite | null; acquiredQuantity?: number; remainingQuantity?: number}
export interface BasketSourceDto {id: string; name: string; unitId: string | null; imageUrl?: string | null; unitShortName: string; locationId: string; locationName: string; quantity: number; localGroupQuantity: number; trackingEnabled: boolean; minimumQuantity: number | null; recommendationActive: boolean}
export interface BasketSource {id: string; name: string; unitId: string | null; imageUrl?: string | null; unitShortName?: string; locationId?: string; locationName?: string; quantity?: number; localGroupQuantity?: number; trackingEnabled?: boolean; minimumQuantity?: number | null; recommendationActive?: boolean}

function mapSource(dto: BasketSourceDto): BasketSource {
  return {id: dto.id, name: dto.name, unitId: dto.unitId, imageUrl: dto.imageUrl ?? null,
    unitShortName: dto.unitShortName, locationId: dto.locationId, locationName: dto.locationName,
    quantity: dto.quantity, localGroupQuantity: dto.localGroupQuantity, trackingEnabled: dto.trackingEnabled,
    minimumQuantity: dto.minimumQuantity, recommendationActive: dto.recommendationActive};
}

export function projectOutstandingBasketRows(rows: readonly BasketNeed[], filter = '', locale?: string, barcodeFilter = ''): BasketNeed[] {
  const search = filter.trim().toLocaleLowerCase();
  const matching = rows.filter(row => !row.cancelled
    && (row.remainingQuantity ?? row.quantity) > 0
    && matchesTaggedText(row, search)
    && (!barcodeFilter.trim() || row.barcode?.includes(barcodeFilter.trim())));
  const rank = (row: BasketNeed) => ({high: 0, medium: 1, low: 2})[row.priority];
  const compare = (a: BasketNeed, b: BasketNeed) => rank(a) - rank(b) || basketCreatedTime(b) - basketCreatedTime(a) || a.name.localeCompare(b.name, locale) || a.id.localeCompare(b.id);
  const manual = matching.filter(row => !row.automatic);
  const automatic = new Map<string, BasketNeed[]>();
  for (const row of matching.filter(row => row.automatic)) {
    if (!row.productGroupId) continue;
    const measurement = row.presentationMeasurement;
    const key = `${row.productGroupId}|${row.unitId}|${row.contextOwnerId ?? ''}|${row.shared === true}`
      + (measurement ? `|${measurement.unitId}|${measurement.packagingQuantity ?? ''}|${measurement.packagingUnitId ?? ''}` : '');
    automatic.set(key, [...(automatic.get(key) ?? []), row]);
  }
  return [...manual, ...[...automatic.entries()].map(([key, targets]) => ({
    ...targets[0], id: `automatic:${key}`, targets: [...targets].sort(compare),
    imageUrl: targets.find(target => !!target.imageUrl)?.imageUrl ?? null,
    createdAt: [...targets].sort((a, b) => basketCreatedTime(b) - basketCreatedTime(a))[0].createdAt,
    quantity: targets.reduce((sum, target) => sum + target.quantity, 0),
    availableQuantity: targets.every(target => target.availableQuantity != null) ? targets.reduce((sum, target) => sum + target.availableQuantity!, 0) : null,
    stockQuantity: targets.reduce((sum, target) => sum + target.stockQuantity, 0),
    acquiredQuantity: targets.reduce((sum, target) => sum + (target.acquiredQuantity ?? 0), 0),
    remainingQuantity: targets.reduce((sum, target) => sum + (target.remainingQuantity ?? target.quantity), 0),
    priority: [...targets].sort(compare)[0].priority,
    targetContainerId: null, targetContainerName: null,
  }))].sort(compare);
}

function basketCreatedTime(need: BasketNeed): number {
  const timestamp = Date.parse(need.createdAt ?? '');
  return Number.isFinite(timestamp) ? timestamp : 0;
}

export function basketTabRows(rows: readonly BasketNeed[], tab: BasketTab): BasketNeed[] {
  if (tab === 'all') return [...new Map(rows.map(row => [row.id, row])).values()];
  if (tab === 'automatic') return rows.filter(row => row.automatic);
  if (tab === 'incoming') return rows.filter(row => !row.automatic && row.shared);
  const owned = rows.filter(row => !row.automatic && !row.shared);
  return tab === 'outgoing' ? owned.filter(row => row.manualShared) : owned;
}

export function basketTabPeople(rows: readonly BasketNeed[], tab: BasketTab): BasketNeedRecipient[] {
  const people = tab === 'outgoing' ? rows.reduce<BasketNeedRecipient[]>((people, row) => [...people, ...(row.sharedWith ?? [])], [])
    : rows.filter(row => !!row.contextOwnerId).map(row => ({userId: row.contextOwnerId!, displayName: row.contextOwnerName ?? ''}));
  return [...new Map(people.map(person => [person.userId, person])).values()]
    .sort((a, b) => a.displayName.localeCompare(b.displayName) || a.userId.localeCompare(b.userId));
}

export function filterBasketTabRows(rows: readonly BasketNeed[], priority: BasketPriorityFilter, personId: string, tab: BasketTab): BasketNeed[] {
  return rows.filter(row => (priority === 'all' || row.priority === priority)
    && (!personId || (tab === 'outgoing' ? row.sharedWith?.some(person => person.userId === personId) : row.contextOwnerId === personId)));
}

export function basketNeedSections(rows: readonly BasketNeed[], filter = '', locale?: string): {personal: BasketNeed[]; shared: BasketNeed[]} {
  const visible = projectOutstandingBasketRows(rows, filter, locale);
  return {personal: visible.filter(row => !row.shared && !row.manualShared),
    shared: visible.filter(row => row.shared || row.manualShared)};
}

export function basketBudget(rows: readonly BasketNeed[], units: BasketUnit[] = []): {totals: {currencyCode: string; amount: string}[]; unpriced: number} {
  const totals = new Map<string, string>();
  let unpriced = 0;
  for (const item of rows) for (const row of item.targets ?? [item]) {
    if (row.cancelled || (row.remainingQuantity ?? row.quantity) <= 0) continue;
    const price = row.plannedPrice;
    const amount = basketNeedPlannedCost(row, units);
    if (!price || amount === null) { unpriced++; continue; }
    totals.set(price.currencyCode, addBasketAmounts(totals.get(price.currencyCode) ?? '0', amount));
  }
  return {totals: [...totals].sort(([a], [b]) => a.localeCompare(b)).map(([currencyCode, amount]) => ({currencyCode, amount})), unpriced};
}

function basketNeedPlannedCost(need: BasketNeed, units: BasketUnit[]): string | null {
  const price = need.plannedPrice;
  if (!price || price.basisQuantity !== '1') return null;
  const quantity = need.remainingQuantity ?? need.quantity;
  if (price.basisUnitId === need.unitId) return calculatePriceTotal(price.amount, String(quantity));
  if (!need.presentationMeasurement?.isPackagingCorrection || basketDisplayUnit(need, units)?.id !== price.basisUnitId) return null;
  const displayedQuantity = basketDisplayQuantity(quantity, need, units);
  return displayedQuantity === null ? null : calculatePriceTotal(price.amount, String(displayedQuantity));
}

function addBasketAmounts(a: string, b: string): string {
  const scale = Math.max(a.split('.')[1]?.length ?? 0, b.split('.')[1]?.length ?? 0);
  const integer = (value: string) => BigInt(value.replace('.', '').padEnd(value.split('.')[0].length + scale, '0'));
  const sum = (integer(a) + integer(b)).toString().padStart(scale + 1, '0');
  return scale ? sum.slice(0, -scale) + '.' + sum.slice(-scale) : sum;
}

export function mapNeed(dto: BasketNeedDto): BasketNeed {
  if (!dto || typeof dto.id !== 'string' || typeof dto.name !== 'string' || !Number.isFinite(dto.quantity) || !Number.isInteger(dto.version)) throw new Error('Invalid need response');
  if (!isBasketPriority(dto.priority)) throw new Error('Invalid v2 need priority');
  return {tags: (dto.tags ?? []).map(mapTag), id: dto.id, itemId: dto.itemId, name: dto.name, barcode: dto.barcode ?? null, createdAt: dto.createdAt, contextOwnerName: dto.contextOwnerName ?? null,
    sharedWith: (dto.sharedWith ?? []).map(person => ({userId: person.userId, displayName: person.displayName})), imageUrl: dto.imageUrl ?? null, sourceItemId: dto.sourceItemId ?? null, sourceContainerId: dto.sourceContainerId ?? null, unitId: dto.unitId ?? '', quantity: dto.quantity,
    product: dto.product ? {barcode: dto.product.barcode, description: dto.product.description, images: dto.product.images.map(image => ({id: image.id, originalUrl: image.originalUrl, thumbnailUrl: image.thumbnailUrl, isMain: image.isMain, useInAi: image.useInAi}))} : null,
    packagingQuantity: dto.packagingQuantity ?? null, packagingUnitId: dto.packagingUnitId ?? null, canEditUnit: dto.canEditUnit === true,
    presentationMeasurement: dto.presentationMeasurement ? {...dto.presentationMeasurement} : null,
    plannedPrice: mapItemPrice(dto.plannedPrice),
    stockQuantity: dto.stockQuantity, availableQuantity: dto.availableQuantity ?? null, version: dto.version, cancelled: dto.isCancelled, plannedShopId: dto.plannedShopId ?? null, comment: dto.comment ?? null, acquiredQuantity: dto.acquiredQuantity ?? 0, remainingQuantity: dto.remainingQuantity ?? dto.quantity,
    automatic: dto.isAutomatic === true, manualShared: dto.isManualShared === true, productGroupId: dto.productGroupId ?? null, priority: dto.priority, priorityMode: dto.priorityMode ?? (dto.isAutomatic ? 'automatic' : 'manual'), canEditPriority: dto.canEditPriority === true, targetContainerId: dto.targetContainerId ?? null,
    targetContainerName: dto.targetContainerName ?? null, contextOwnerId: dto.contextOwnerId, shared: dto.isShared === true,
    canAcquire: dto.canAcquire === true, canCancel: dto.canCancel, desiredQuantity: dto.desiredQuantity ?? null};
}

@Injectable({providedIn: 'root'})
export class BasketService {
  generateSuggestion(request: {name: string; currentDescription: string; quantity: string; unitLabel?: string; images: File[]}): Observable<{suggestedName?: string | null; suggestedDescription: string; model: string}> {
    const body = new FormData();
    body.append('name', request.name);
    body.append('currentDescription', request.currentDescription);
    if (request.quantity) body.append('quantity', request.quantity);
    if (request.unitLabel) body.append('unitLabel', request.unitLabel);
    for (const image of request.images) body.append('images', image, image.name);
    return this.http.post<ApiResponse<{suggestedName?: string | null; suggestedDescription: string; model: string}>>(
      buildApiUrl('/v1/me/basket/ai-suggestion'), body, this.options(['VALIDATION_ERROR'])).pipe(map(response => {
        if (!response.data) throw new Error('Missing AI suggestion');
        return response.data;
      }));
  }
  private readonly http = inject(HttpClient);
  private readonly navigationCountSubject = new BehaviorSubject<number | null>(null);
  private readonly changesSubject = new Subject<void>();
  private navigationAccountId = '';
  private navigationCountRefreshAccountId: string | null = null;
  private navigationCountRefreshPendingAccountId: string | null = null;
  private navigationProjectionRequestId = 0;
  readonly navigationCount$ = this.navigationCountSubject.asObservable();
  readonly changes$ = this.changesSubject.asObservable();
  private options(codes: readonly LocalApiErrorCode[] = []) {
    return {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, codes)};
  }
  updatePriority(id: string, version: number, priorityChange: BasketPriorityChange): Observable<BasketNeed> {
    return this.http.put<ApiResponse<BasketNeedDto>>(buildApiUrl(`/v2/me/basket/needs/${id}/priority`),
      {version, priorityChange}, this.options(['BadRequest', 'Conflict', 'RecordNotFound']))
      .pipe(map(response => mapNeed(response.data!)), tap(() => this.changesSubject.next()));
  }
  needHistory(id: string, cursor: string | null = null): Observable<BasketNeedHistoryPage> {
    const params: Record<string, string> = {limit: '20'};
    if (cursor) params['cursor'] = cursor;
    return this.http.get<ApiResponse<BasketNeedHistoryPageDto>>(buildApiUrl(`/v2/me/basket/needs/${id}/history`),
      {...this.options(['RecordNotFound']), params}).pipe(map(response => ({needId: response.data!.needId, name: response.data!.name, contextOwnerId: response.data!.contextOwnerId, nextCursor: response.data!.nextCursor, items: response.data!.items.map(event =>
        ({id: event.id, createdAt: event.createdAt, actorId: event.actorId, actorName: event.actorName, reason: event.reason, version: event.version, unitId: event.unitId, presentationMeasurement: event.presentationMeasurement ? {...event.presentationMeasurement} : null, changes: event.changes.map(change => ({field: change.field, before: change.before, after: change.after}))}))})));
  }
  purchase(id: string) {
    return this.http.get<ApiResponse<BasketPurchaseDto>>(buildApiUrl(`/v1/me/basket/purchases/${id}`),
      this.options(['RecordNotFound'])).pipe(map(response => mapPurchase(response.data!)));
  }
  journal(cursor: string | null = null, limit = 20, query = '', tags: TagSelection = {authorIds: [], allAuthors: false}): Observable<BasketPurchaseJournalPage> {
    const params: Record<string, string | string[]> = {limit: String(limit), query, tagAuthorIds: tags.authorIds, allTagAuthors: String(tags.allAuthors)};
    if (cursor) params['cursor'] = cursor;
    return this.http.get<ApiResponse<BasketPurchaseJournalPageDto>>(buildApiUrl('/v1/me/basket/journal'), {...this.options(), params})
      .pipe(map(response => {
        const data = response.data;
        if (!data || !Array.isArray(data.items) || !Array.isArray(data.units) || !Number.isInteger(data.totalCount))
          throw new Error('Invalid purchase journal response');
        return {items: data.items.map(mapPurchase), units: data.units, totalCount: data.totalCount, nextCursor: data.nextCursor ?? null};
      }));
  }
  journalPurchase(id: string): Observable<BasketPurchaseJournalDetail> {
    return this.http.get<ApiResponse<BasketPurchaseJournalDetailDto>>(buildApiUrl(`/v1/me/basket/journal/${id}`),
      this.options(['RecordNotFound']))
      .pipe(map(response => {
        const data = response.data;
        if (!data?.purchase || !data.unit) throw new Error('Invalid purchase journal detail response');
        return {purchase: mapPurchase(data.purchase), unit: data.unit, contentUnit: data.contentUnit ?? null};
      }));
  }
  targets() {
    return this.http.get<ApiResponse<BasketTarget[]>>(buildApiUrl('/v1/me/basket/targets'), this.options())
      .pipe(map(response => (response.data ?? []).map(x => ({id: x.id, name: x.name, parentId: x.parentId}))));
  }
  acquire(data: BasketAcquire) {
    return this.http.post<ApiResponse<BasketPurchaseDto>>(buildApiUrl('/v1/me/basket/purchases'), data,
      this.options(['VALIDATION_ERROR', 'BadRequest', 'Conflict', 'RecordNotFound', 'FeatureUnavailable'])).pipe(
      map(response => mapPurchase(response.data!)), tap(() => this.changesSubject.next()));
  }
  needAccess(id: string): Observable<BasketNeedAccess> {
    return this.http.get<ApiResponse<BasketNeedAccessDto>>(buildApiUrl(`/v2/me/basket/needs/${id}/access`),
      this.options(['RecordNotFound']))
      .pipe(map(response => mapNeedAccess(response.data!)));
  }
  setNeedAccess(id: string, data: BasketNeedAccessWrite): Observable<BasketNeedAccess> {
    return this.http.put<ApiResponse<BasketNeedAccessDto>>(buildApiUrl(`/v2/me/basket/needs/${id}/access`), data,
      this.options(['Conflict']))
      .pipe(map(response => mapNeedAccess(response.data!)), tap(() => this.changesSubject.next()));
  }
  transfer(data: BasketTransferWrite): Observable<BasketPurchase> {
    return this.http.post<ApiResponse<BasketPurchaseDto>>(buildApiUrl(`/v1/me/basket/purchases/${data.purchaseId}/transfer`), data,
      this.options(['BadRequest', 'Conflict', 'RecordNotFound']))
      .pipe(map(response => mapPurchase(response.data!)), tap(() => this.changesSubject.next()));
  }
  correctPurchase(data: BasketPurchaseCorrection) {
    return this.http.put<ApiResponse<BasketPurchaseDto>>(buildApiUrl(`/v1/me/basket/purchases/${data.purchaseId}`), data,
      this.options(['BadRequest', 'Conflict', 'RecordNotFound']))
      .pipe(map(response => mapPurchase(response.data!)), tap(() => this.changesSubject.next()));
  }
  place(data: BasketPlace) {
    return this.http.post<ApiResponse<{purchase: BasketPurchaseDto; placedItemId: string}>>(buildApiUrl('/v1/me/basket/placements'), data,
      this.options(['BadRequest', 'Conflict', 'RecordNotFound']))
      .pipe(map(response => ({purchase: mapPurchase(response.data!.purchase), placedItemId: response.data!.placedItemId})),
        tap(() => this.changesSubject.next()));
  }
  consume(data: BasketConsume) {
    return this.http.post<ApiResponse<BasketPurchaseDto>>(buildApiUrl(`/v1/me/basket/purchases/${data.purchaseId}/consumptions`), data,
      this.options(['BadRequest', 'Conflict', 'RecordNotFound']))
      .pipe(map(response => mapPurchase(response.data!)), tap(() => this.changesSubject.next()));
  }
  purchaseTemplates(barcode: string): Observable<BasketPurchaseTemplate[]> {
    return this.http.get<ApiResponse<BasketPurchaseTemplateDto[]>>(buildApiUrl('/v1/me/basket/purchase-templates'),
      {...this.options(), params: {barcode}}).pipe(map(response => (response.data ?? []).map(x => ({
      id: x.purchaseId, name: x.name, unitId: x.unitId, barcode: x.barcode,
      description: x.description ?? null, packagingQuantity: x.packagingQuantity ?? null,
      packagingUnitId: x.packagingUnitId ?? null, images: x.images ?? [],
    }))));
  }
  list(tags: TagSelection = {authorIds: [], allAuthors: false}): Observable<Basket> {
    const navigationAccountId = this.navigationAccountId;
    const navigationProjectionRequestId = ++this.navigationProjectionRequestId;
    return this.http.get<ApiResponse<BasketDto>>(buildApiUrl('/v2/me/basket'), {...this.options(), params: {tagAuthorIds: tags.authorIds, allTagAuthors: tags.allAuthors}}).pipe(map(response => {
      const dto = response.data;
      if (!dto || !Array.isArray(dto.items) || !Array.isArray(dto.units)) throw new Error('Missing basket');
      const basket = {containerId: dto.containerId, canCreate: dto.canCreate, items: dto.items.map(mapNeed),
        purchases: (dto.purchases ?? []).map(mapPurchase),
        purchaseLimit: dto.purchaseLimit ? {currentCount: dto.purchaseLimit.currentCount, limit: dto.purchaseLimit.limit,
          unlimited: dto.purchaseLimit.isUnlimited, reached: dto.purchaseLimit.isLimitReached, resetsAt: dto.purchaseLimit.resetsAt} : null,
        units: dto.units.map(unit => ({id: unit.id, name: unit.name, shortName: unit.shortName, isDefault: unit.isDefault, isActive: unit.isActive, isQuantityUnit: unit.isQuantityUnit, supportsPackaging: unit.supportsPackaging, isPackagingUnit: unit.isPackagingUnit, allowedPackagingUnitIds: unit.allowedPackagingUnitIds ?? null, kind: unit.kind, baseUnitId: unit.baseUnitId, baseUnitFactor: unit.baseUnitFactor}))} satisfies Basket;
      if (navigationAccountId && navigationAccountId === this.navigationAccountId
        && navigationProjectionRequestId === this.navigationProjectionRequestId) {
        this.navigationCountSubject.next(projectOutstandingBasketRows(basket.items).length);
      }
      return basket;
    }), tap({error: () => {
      if (navigationAccountId && navigationAccountId === this.navigationAccountId
        && navigationProjectionRequestId === this.navigationProjectionRequestId) {
        this.navigationCountSubject.next(null);
      }
    }}));
  }
  get(id: string) {
    return this.http.get<ApiResponse<BasketNeedDto>>(buildApiUrl(`/v2/me/basket/needs/${id}`),
      this.options(['RecordNotFound'])).pipe(map(response => mapNeed(response.data!)));
  }
  suggestedPrice(sourceItemId: string, unitId: string): Observable<ItemPrice | null> {
    return this.http.get<ApiResponse<ItemPriceDto | null>>(buildApiUrl('/v1/me/basket/suggested-price'),
      {...this.options(), params: {sourceItemId, unitId}}).pipe(map(response => mapItemPrice(response.data)));
  }
  updatePlannedPrice(id: string, version: number, plannedPrice: ItemPrice | null): Observable<BasketNeed> {
    return this.http.put<ApiResponse<BasketNeedDto>>(buildApiUrl(`/v2/me/basket/needs/${id}/planned-price`),
      {version, plannedPrice}, this.options(['BadRequest', 'Conflict', 'RecordNotFound', 'FeatureUnavailable']))
      .pipe(map(response => mapNeed(response.data!)), tap(() => this.changesSubject.next()));
  }
  sources(query: string, intent = 'text', visibility = 'all'): Observable<BasketSource[]> {
    return this.http.get<ApiResponse<BasketSourceDto[]>>(buildApiUrl('/v1/me/basket/sources'), {...this.options(), params: {query, intent, visibility}})
      .pipe(map(response => (response.data ?? []).map(mapSource)));
  }
  replenishmentSources(needId: string): Observable<BasketSource[]> {
    return this.http.get<ApiResponse<BasketSourceDto[]>>(buildApiUrl(`/v1/me/basket/needs/${needId}/sources`), this.options())
      .pipe(map(response => (response.data ?? []).map(mapSource)));
  }
  replenish(data: BasketReplenish) {
    return this.http.post<ApiResponse<BasketReplenishResult>>(buildApiUrl('/v1/me/basket/replenishments'), data,
      this.options(['BadRequest', 'Conflict', 'RecordNotFound', 'FeatureUnavailable']))
      .pipe(map(response => response.data!), tap(() => this.changesSubject.next()));
  }
  cancel(need: BasketNeed) {
    return this.http.delete<ApiResponse<BasketNeedDto>>(buildApiUrl(`/v2/me/basket/needs/${need.id}`),
      {...this.options(['BadRequest', 'Conflict', 'RecordNotFound']), params: {version: need.version}})
      .pipe(map(response => mapNeed(response.data!)), tap(() => this.changesSubject.next()));
  }
  save(write: BasketWrite, images: File[] = []) {
    const data = {id: write.id, name: write.name, unitId: write.unitId, quantity: write.quantity,
      sourceItemId: write.sourceItemId, plannedShopId: write.plannedShopId, comment: write.comment,
      priorityChange: write.priorityChange, version: write.version, measurement: write.measurement,
      plannedPrice: write.plannedPrice, clearPlannedPrice: write.clearPlannedPrice,
      product: write.product ? mapBasketProduct(write.product) : undefined};
    let body: typeof data | FormData = data;
    if (images.length) {
      body = new FormData();
      body.append('data', JSON.stringify(data));
      for (const image of images) body.append('images', image, image.name);
    }
    const request = write.version === null
      ? this.http.post<ApiResponse<BasketNeedDto>>(buildApiUrl('/v2/me/basket/needs'),
        body, this.options(['VALIDATION_ERROR', 'BadRequest', 'Conflict', 'FeatureUnavailable', ...PACKAGING_ERROR_CODES]))
      : this.http.put<ApiResponse<BasketNeedDto>>(buildApiUrl(`/v2/me/basket/needs/${write.id}`), body,
        this.options(['VALIDATION_ERROR', 'BadRequest', 'Conflict', 'RecordNotFound', 'FeatureUnavailable', ...PACKAGING_ERROR_CODES]));
    return request.pipe(map(response => mapNeed(response.data!)), tap(() => this.changesSubject.next()));
  }

  refreshNavigationCount(): void {
    const accountId = this.navigationAccountId;
    if (!accountId) return;
    if (this.navigationCountRefreshAccountId === accountId) {
      this.navigationCountRefreshPendingAccountId = accountId;
      return;
    }
    this.navigationCountRefreshAccountId = accountId;
    this.list().pipe(finalize(() => {
      if (this.navigationCountRefreshAccountId === accountId) this.navigationCountRefreshAccountId = null;
      if (this.navigationCountRefreshPendingAccountId === accountId && this.navigationAccountId === accountId) {
        this.navigationCountRefreshPendingAccountId = null;
        this.refreshNavigationCount();
      }
    })).subscribe({error: () => undefined});
  }

  setNavigationAccount(accountId: string): void {
    if (accountId === this.navigationAccountId) return;
    this.navigationAccountId = accountId;
    this.navigationProjectionRequestId++;
    this.navigationCountRefreshPendingAccountId = null;
    this.navigationCountSubject.next(null);
  }
}






export interface BasketTarget {id: string; name: string; parentId: string | null}
export interface BasketPurchasePrice {amount: string; currencyCode: string; basisQuantity: string; basisUnitId: string}
export interface BasketAcquire {id: string; needId: string; quantity: number; unitId: string; needVersion: number; shopId: string | null; comment: string | null; price?: BasketPurchasePrice | null; barcode?: string | null}
export interface BasketNeedAccessDto {needId: string; version: number; contactUserIds: string[]}
export interface BasketNeedAccess {needId: string; version: number; contactUserIds: string[]}
export interface BasketNeedAccessWrite {operationId: string; version: number; contactUserIds: string[]; notifyUsers: boolean}
export interface BasketTransferWrite {operationId: string; purchaseId: string; version: number}
export function mapNeedAccess(dto: BasketNeedAccessDto): BasketNeedAccess {
  if (!dto || typeof dto.needId !== 'string' || !Number.isInteger(dto.version) || !Array.isArray(dto.contactUserIds))
    throw new Error('Invalid need access response');
  return {needId: dto.needId, version: dto.version, contactUserIds: dto.contactUserIds.filter(id => typeof id === 'string')};
}
export interface BasketPlace {operationId: string; purchaseId: string; destinationId: string; quantity: number; unitId: string; version: number}
export interface BasketConsume {operationId: string; purchaseId: string; quantity: number; unitId: string; version: number; mode?: 'packages' | 'content'; amount?: number}
export interface BasketPurchaseTemplateDto {purchaseId: string; name: string; unitId: string; barcode: string;
  description: string | null; packagingQuantity: number | null; packagingUnitId: string | null;
  images: {id: string; originalUrl: string; thumbnailUrl: string | null; isMain: boolean; useInAi: boolean}[]}
export interface BasketPurchaseTemplate {id: string; name: string; unitId: string; barcode: string;
  description: string | null; packagingQuantity: number | null; packagingUnitId: string | null;
  images: {id: string; originalUrl: string; thumbnailUrl: string | null; isMain: boolean; useInAi: boolean}[]}
export interface BasketPurchaseCorrection {operationId: string; purchaseId: string; quantity: number; version: number;
  price: BasketPurchasePrice | null; shopId: string | null}
export interface BasketReplenish {needId: string; sourceItemId: string; quantity: number; unitId: string; expectedSourceQuantity: number; disableSourceTrackingWhenEmpty?: boolean | null}
export interface BasketReplenishResult {sourceItemId: string; placedItemId: string; remainingSourceQuantity: number; recommendationChanged: boolean}
export function mapPurchase(dto: BasketPurchaseDto): BasketPurchase {
  if (!dto || typeof dto.id !== 'string' || !Number.isFinite(dto.remainingQuantity) || !Number.isInteger(dto.version)) throw new Error('Invalid purchase response');
  return {tags: (dto.tags ?? []).map(mapTag), id: dto.id, needId: dto.needId, itemId: dto.itemId, name: dto.name, unitId: dto.unitId, quantity: dto.quantity,
    placedQuantity: dto.placedQuantity, consumedQuantity: dto.consumedQuantity ?? 0,
    consumedContent: dto.consumedContent ?? 0, packagingQuantity: dto.packagingQuantity ?? null,
    packagingUnitId: dto.packagingUnitId ?? null, remainingContent: dto.remainingContent ?? null,
    remainingQuantity: dto.remainingQuantity, acquiredAt: dto.acquiredAt,
    version: dto.version, shopId: dto.shopId, shopName: dto.shopName, comment: dto.comment,
    authorUserId: dto.authorUserId, authorUsername: dto.authorUsername ?? null,
    priceAmount: dto.priceAmount ?? null, currencyCode: dto.currencyCode ?? null,
    priceBasisQuantity: dto.priceBasisQuantity ?? null, priceBasisUnitId: dto.priceBasisUnitId ?? null,
    sourceItemId: dto.sourceItemId ?? null, sourceContainerId: dto.sourceContainerId ?? null, isShared: dto.isShared === true,
    canPlace: dto.canPlace !== false, canConsume: dto.canConsume === true, imageUrl: dto.imageUrl ?? null,
    requesterUserId: dto.requesterUserId ?? null, transferredAt: dto.transferredAt ?? null, canTransfer: dto.canTransfer === true};
}
export function compatibleBasketUnits(unit: BasketUnit, original?: BasketUnit): boolean {
  return !!original && (unit.id === original.id || (!!unit.baseUnitId && unit.baseUnitId === original.baseUnitId && unit.kind === original.kind
    && (unit.baseUnitFactor ?? 0) > 0 && (original.baseUnitFactor ?? 0) > 0));
}
export function convertBasketQuantity(value: string, unit?: BasketUnit, original?: BasketUnit, outputPrecision = 3): number | null {
  if (!unit || !original || !compatibleBasketUnits(unit, original) || !/^\d+(?:\.\d{1,3})?$/.test(value)) return null;
  const amount = Number(value);
  if (amount <= 0 || amount >= 1e15) return null;
  const result = unit.id === original.id ? amount : amount * unit.baseUnitFactor! / original.baseUnitFactor!;
  const scale = 10 ** outputPrecision;
  const rounded = Math.round(result * scale) / scale;
  return rounded > 0 && result < 1e15 && Math.abs(result - rounded) < 1e-9 ? rounded : null;
}

export function compatibleBasketPresentation(unit: BasketUnit, original?: BasketUnit, measurement?: BasketPresentationMeasurement | null): boolean {
  return compatibleBasketUnits(unit, original) || !!(original && measurement?.isPackagingCorrection &&
    measurement.unitId === unit.id && unit.supportsPackaging && original.supportsPackaging &&
    unit.baseUnitFactor === 1 && original.baseUnitFactor === 1);
}

export function convertBasketPresentationQuantity(value: string, unit?: BasketUnit, original?: BasketUnit,
  measurement?: BasketPresentationMeasurement | null, precision = 3): number | null {
  if (!unit || !original || !compatibleBasketPresentation(unit, original, measurement)) return null;
  return convertBasketQuantity(value, unit, compatibleBasketUnits(unit, original) ? original : unit, precision);
}

export function initialBasketPurchaseQuantity(quantity: number, original: BasketUnit | undefined, units: BasketUnit[], automatic: boolean, measurement?: BasketPresentationMeasurement | null): {quantity: string; unitId: string} {
  const initial = {quantity: String(quantity), unitId: original?.id ?? ''};
  if (!automatic || !original || !measurement) return initial;
  const unit = units.find(candidate => candidate.id === measurement.unitId && candidate.isActive !== false && compatibleBasketPresentation(candidate, original, measurement));
  if (unit) {
    const amount = unit.id === original.id ? quantity : quantity * original.baseUnitFactor! / unit.baseUnitFactor!;
    const input = String(Math.round(amount * 1000) / 1000);
    const converted = convertBasketPresentationQuantity(input, unit, original, measurement, 6);
    if (converted !== null && Math.abs(converted - quantity) < 1e-9) return {quantity: input, unitId: unit.id};
    return {quantity: '', unitId: unit.id};
  }
  return initial;
}

export function basketDisplayUnit(need: Pick<BasketNeed, 'unitId' | 'presentationMeasurement'>, units: BasketUnit[]): BasketUnit | undefined {
  const original = units.find(unit => unit.id === need.unitId);
  const preferred = units.find(unit => unit.id === need.presentationMeasurement?.unitId);
  return preferred && compatibleBasketPresentation(preferred, original, need.presentationMeasurement) ? preferred : original;
}

export function basketDisplayQuantity(quantity: number | null | undefined, need: Pick<BasketNeed, 'unitId' | 'presentationMeasurement'>, units: BasketUnit[]): number | null {
  if (quantity == null) return null;
  const original = units.find(unit => unit.id === need.unitId);
  const display = basketDisplayUnit(need, units);
  return original && display && display.id !== original.id
    ? quantity * original.baseUnitFactor! / display.baseUnitFactor! : quantity;
}
